import { Link } from "react-router-dom";
import { Phone, Mail, ChevronRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PHONE, PHONE_TEL, EMAIL, EMAIL_MAILTO } from "../seo/routes";

export const SectionHead = ({ overline, title, sub }) => (
  <div className="mb-10">
    {overline && <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#FF5500] mb-3">{overline}</p>}
    <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight uppercase text-white">{title}</h2>
    {sub && <p className="mt-3 text-base text-gray-400 max-w-3xl leading-relaxed">{sub}</p>}
  </div>
);

export const CTABand = ({ title, sub }) => (
  <section className="bg-[#131620] border-y border-[#FF5500]/20" data-testid="cta-band">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 flex flex-col md:flex-row md:items-center justify-between gap-6">
      <div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase text-white tracking-tight">{title}</h2>
        <p className="mt-2 text-gray-400 text-sm sm:text-base max-w-xl">{sub}</p>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 shrink-0">
        <a
          href={PHONE_TEL}
          data-testid="cta-phone-button"
          className="inline-flex items-center justify-center gap-2 bg-[#FF5500] hover:bg-[#E04800] text-white font-semibold px-6 py-3 rounded-sm transition-colors duration-200"
        >
          <Phone className="h-4 w-4" /> {PHONE}
        </a>
        <a
          href={EMAIL_MAILTO}
          data-testid="cta-email-button"
          className="inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/15 text-white font-medium px-6 py-3 rounded-sm transition-colors duration-200"
        >
          <Mail className="h-4 w-4 text-[#FF5500]" /> {EMAIL}
        </a>
      </div>
    </div>
  </section>
);

export const FAQBlock = ({ faqs, heading = "Frequently Asked Questions" }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="faq-section">
    <SectionHead overline="Answers" title={heading} />
    <Accordion type="single" collapsible className="max-w-3xl">
      {faqs.map((f, i) => (
        <AccordionItem key={i} value={`faq-${i}`} className="border-white/10" data-testid={`faq-item-${i}`}>
          <AccordionTrigger className="text-left text-gray-100 hover:text-[#FF5500] text-sm sm:text-base" data-testid={`faq-question-${i}`}>
            {f.q}
          </AccordionTrigger>
          <AccordionContent className="text-gray-400 text-sm leading-relaxed" data-testid={`faq-answer-${i}`}>
            {f.a}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
);

export const LinkGrid = ({ title, overline, items, cols = "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6" }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid={`linkgrid-${(overline || title).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <SectionHead overline={overline} title={title} />
    <div className={`grid ${cols} gap-3`}>
      {items.map((it) => (
        <Link
          key={it.to}
          to={it.to}
          data-testid={it.testid}
          className="group border border-white/10 bg-[#181C28] hover:border-[#FF5500]/50 rounded-sm px-4 py-3 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(255,85,0,0.15)]"
        >
          <span className="text-sm text-gray-300 group-hover:text-white transition-colors flex items-center justify-between gap-1">
            {it.label}
            <ChevronRight className="h-3.5 w-3.5 text-[#FF5500] opacity-0 group-hover:opacity-100 transition-opacity" />
          </span>
        </Link>
      ))}
    </div>
  </section>
);

export const Breadcrumbs = ({ items }) => (
  <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6" aria-label="Breadcrumb" data-testid="breadcrumbs">
    <ol className="flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
      {items.map((it, i) => (
        <li key={it.to || i} className="flex items-center gap-1.5">
          {i > 0 && <ChevronRight className="h-3 w-3 text-gray-600" />}
          {it.to && i < items.length - 1 ? (
            <Link to={it.to} data-testid={`breadcrumb-${i}`} className="hover:text-[#FF5500] transition-colors">
              {it.label}
            </Link>
          ) : (
            <span className="text-gray-300">{it.label}</span>
          )}
        </li>
      ))}
    </ol>
  </nav>
);

export const PageHero = ({ overline, title, lead }) => (
  <section className="relative border-b border-white/10 overflow-hidden" data-testid="page-hero">
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
      {overline && <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#FF5500] mb-4">{overline}</p>}
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight uppercase leading-none text-white max-w-4xl">
        {title}
      </h1>
      {lead && <p className="mt-5 text-lg text-gray-300 leading-relaxed max-w-3xl">{lead}</p>}
    </div>
  </section>
);

export const Prose = ({ paragraphs }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="prose-section">
    <div className="max-w-3xl space-y-5">
      {paragraphs.map((p, i) => (
        <p key={i} className="text-base text-gray-400 leading-relaxed">
          {p}
        </p>
      ))}
    </div>
  </section>
);
